import React, { useState, useEffect, useCallback } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Select from "../../../components/ui/Select";
import Input from "../../../components/ui/Input";
import { useJobRoles } from "../../../hooks/useJobRoles";
import { apiClient } from "../../../services/api/client";

const RoleAssignment = ({ employee, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [contractedHours, setContractedHours] = useState("");
  const [effectiveDate, setEffectiveDate] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Role history from API
  const [roleHistory, setRoleHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Load real job roles from the tenant
  const { jobRoles, loading: rolesLoading } = useJobRoles({ activeOnly: true });

  // Build options from real job roles
  const roleOptions = (jobRoles || []).map((role) => ({
    label: `${role.name}${role.department ? ` (${role.department})` : ""}`,
    value: role.id,
    description: role.isEligibleForTronc
      ? "Eligible for TRONC"
      : "Not eligible",
  }));

  // Fetch real role history from backend
  const fetchRoleHistory = useCallback(async () => {
    if (!employee?.id) return;
    setHistoryLoading(true);
    try {
      const response = await apiClient.get(
        `/employees/${employee.id}/role-history`
      );
      if (response.success) {
        setRoleHistory(response.data || []);
      }
    } catch (err) {
      console.error("Failed to fetch role history:", err);
    } finally {
      setHistoryLoading(false);
    }
  }, [employee?.id]);

  useEffect(() => {
    fetchRoleHistory();
  }, [fetchRoleHistory]);

  // Reset form when editing starts
  const handleStartEdit = () => {
    setSelectedRoleId("");
    setContractedHours(employee?.serviceHours || "");
    setEffectiveDate(new Date().toISOString().split("T")[0]);
    setReason("");
    setSaveError(null);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setSaveError(null);
  };

  const handleSaveRole = async () => {
    if (!selectedRoleId || !effectiveDate) {
      setSaveError("Please select a role and effective date.");
      return;
    }

    setSaving(true);
    setSaveError(null);

    try {
      const response = await apiClient.post(
        `/employees/${employee.id}/role-change`,
        {
          newJobRoleId: selectedRoleId,
          effectiveDate: new Date(effectiveDate).toISOString(),
          contractedHoursPerWeek:
            contractedHours !== "" ? Number(contractedHours) : null,
          reason: reason || null,
        }
      );

      if (response.success) {
        setIsEditing(false);
        // Refresh role history
        await fetchRoleHistory();
        // Notify parent to refresh employee data
        if (onUpdate && response.data) {
          onUpdate(response.data);
        }
      } else {
        setSaveError(response.message || "Failed to update role.");
      }
    } catch (err) {
      console.error("Role change failed:", err);
      setSaveError(err.message || "Failed to update role.");
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  // Determine current role name from jobRoles data or employee data
  const currentRoleName =
    employee?.role || employee?.jobTitle || "No role assigned";

  // Calculate eligibility status
  const isEligible = employee?.isEligibleForTronc || employee?.eligibilityStatus === "Eligible";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">
          Role Assignment
        </h3>
        {!isEditing ? (
          <Button
            variant="outline"
            size="sm"
            iconName="Edit"
            onClick={handleStartEdit}
          >
            Modify Role
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              size="sm"
              iconName="Save"
              onClick={handleSaveRole}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        )}
      </div>

      {/* Current Role */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center gap-3 mb-2">
          <Icon name="Briefcase" size={20} color="var(--color-primary)" />
          <h4 className="font-semibold text-gray-900">Current Role</h4>
        </div>
        <p className="text-lg font-medium text-gray-900">{currentRoleName}</p>
        {employee?.serviceHours > 0 && (
          <p className="text-sm text-gray-600 mt-1">
            Contracted: {employee.serviceHours}h/week
          </p>
        )}
      </div>

      {/* Role Change Form */}
      {isEditing && (
        <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
          <h4 className="font-semibold text-gray-900">
            Update Role Assignment
          </h4>

          {saveError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
              {saveError}
            </div>
          )}

          <Select
            label="New Role"
            options={roleOptions}
            value={selectedRoleId}
            onChange={setSelectedRoleId}
            placeholder="Select a job role..."
            searchable
            loading={rolesLoading}
            required
          />

          <Input
            type="number"
            label="Contracted Hours (per week)"
            description="Update the employee's contracted hours if needed"
            value={contractedHours}
            onChange={(e) => setContractedHours(e?.target?.value)}
            min="0"
            max="168"
            step="0.5"
          />

          <Input
            type="date"
            label="Effective Date"
            description="When should this role change take effect?"
            value={effectiveDate}
            onChange={(e) => setEffectiveDate(e?.target?.value)}
            required
          />

          <Input
            label="Reason for Change"
            description="Optional reason (e.g., Promotion, Transfer, Restructuring)"
            value={reason}
            onChange={(e) => setReason(e?.target?.value)}
            placeholder="e.g. Promoted to Senior FOH"
          />

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <Icon
                name="AlertCircle"
                size={16}
                color="var(--color-accent)"
                className="mt-0.5"
              />
              <div className="text-sm text-gray-700">
                <p className="font-medium">Role Change Impact</p>
                <p className="mt-1">
                  Changing roles will automatically recalculate TRONC
                  distribution percentages based on the active rule
                  configuration. Previous role assignment will be saved as
                  history.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Role History */}
      <div>
        <h4 className="text-md font-semibold text-gray-900 mb-4">
          Role History
        </h4>

        {historyLoading ? (
          <div className="card p-6 text-center">
            <div className="spinner mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">
              Loading role history...
            </p>
          </div>
        ) : roleHistory.length === 0 ? (
          <div className="card p-6 text-center">
            <Icon
              name="Briefcase"
              size={28}
              className="mx-auto mb-3 text-muted-foreground"
            />
            <p className="text-sm text-muted-foreground">
              No role history recorded yet.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {roleHistory.map((history) => (
              <div
                key={history.id}
                className={`flex items-center justify-between p-3 rounded-lg border ${
                  !history.endDate
                    ? "bg-blue-50 border-blue-200"
                    : "bg-gray-50 border-gray-200"
                }`}
              >
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-gray-900">
                      {history.jobRoleName}
                    </p>
                    {!history.endDate && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-primary/10 text-primary">
                        Current
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {formatDate(history.startDate)} —{" "}
                    {history.endDate ? formatDate(history.endDate) : "Present"}
                  </p>
                  {history.reason && (
                    <p className="text-xs text-gray-400 mt-0.5 italic">
                      {history.reason}
                    </p>
                  )}
                </div>
                <div className="text-right text-xs text-gray-400">
                  {history.changedBy && (
                    <p>by {history.changedBy.split("@")[0]}</p>
                  )}
                  <p>{formatDate(history.changedOn)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Eligibility Impact */}
      <div
        className={`rounded-lg border p-4 ${
          isEligible
            ? "bg-green-50 border-green-200"
            : "bg-red-50 border-red-200"
        }`}
      >
        <div className="flex items-start gap-3">
          <Icon
            name={isEligible ? "CheckCircle" : "XCircle"}
            size={20}
            className={`mt-0.5 ${isEligible ? "text-success" : "text-error"}`}
          />
          <div>
            <h4 className="font-semibold text-gray-900 mb-1">
              TRONC Eligibility Status
            </h4>
            <p className="text-sm text-gray-700">
              This employee is currently{" "}
              <strong>{isEligible ? "eligible" : "not eligible"}</strong> for
              TRONC distribution.
            </p>
            <div className="mt-2 text-xs text-gray-600 space-y-0.5">
              <p>
                • Contracted hours:{" "}
                {employee?.serviceHours
                  ? `${employee.serviceHours}h/week`
                  : "Not set"}
              </p>
              <p>• Role: {currentRoleName}</p>
              <p>
                • Active status:{" "}
                {employee?.isActive !== false ? "Active" : "Inactive"}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoleAssignment;
